import { useMemo, useState } from "react";
import { PALIA_CATEGORIES } from "../../config/paliaCategories";
import { useSession } from "../../hooks/useSession";
import { supabase } from "../../lib/supabaseClient";
import "./Contribute.css";

const isValidHttpUrl = (value) => {
    if (!value) return true;
    try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
};

const normalizeTagsText = (tagsText) =>
    (tagsText || "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean)
        .join(", ");

export default function Contribute() {
    // ---------- Auth ----------
    const { session, loading: authLoading } = useSession();

    // Keep this stable, and always have a fallback
    const categories = useMemo(
        () =>
            Array.isArray(PALIA_CATEGORIES) && PALIA_CATEGORIES.length
                ? PALIA_CATEGORIES
                : ["Other"],
        [],
    );

    const emptyForm = useMemo(
        () => ({
            term: "",
            definition: "",
            category: categories[0] || "Other",
            tags: "",
            palia_pedia_u: "",
        }),
        [categories],
    );

    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [msg, setMsg] = useState("");
    const [loading, setLoading] = useState(false);

    const onFieldChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const validateForm = (f) => {
        const errs = {};

        if (!f.term.trim()) errs.term = "Term is required.";
        if (!f.definition.trim()) errs.definition = "Definition is required.";

        // Category must be selected from allowed set
        if (!f.category || !categories.includes(f.category)) {
            errs.category = "Please choose a valid category.";
        }

        if (f.palia_pedia_u && !isValidHttpUrl(f.palia_pedia_u.trim())) {
            errs.palia_pedia_u = "Link must be a valid http(s) URL.";
        }

        return errs;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setMsg("");
        setErrors({});

        if (!session?.user) {
            setErrors({ submit: "You must be logged in to contribute." });
            return;
        }

        const validationErrors = validateForm(form);
        if (Object.keys(validationErrors).length) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);

        const payload = {
            term: form.term.trim(),
            definition: form.definition.trim(),
            category: form.category, // already validated
            tags: normalizeTagsText(form.tags),
            palia_pedia_u: form.palia_pedia_u.trim() || null,
            approved: false,
        };

        const { error } = await supabase
            .from("term_submissions")
            .insert([payload]);

        setLoading(false);

        if (error) {
            setErrors({ submit: error.message });
            return;
        }

        setForm(emptyForm);
        setMsg(
            "Submitted! Thanks — your term will be reviewed before it goes live.",
        );
    };

    const onClear = () => {
        setForm(emptyForm);
        setErrors({});
        setMsg("");
    };

    // Optional: avoid flicker while session loads
    if (authLoading) return null;

    // ---------- NOT LOGGED IN ----------
    if (!session?.user) {
        return (
            <div className="contribute-container">
                <div className="contribute-card">
                    <h2>Log in to contribute</h2>
                    <p className="contribute-subtext">
                        You need an account to submit terms and receive credit
                        for your work.
                    </p>

                    <div className="contribute-actions">
                        <a href="/login" className="contribute-login-btn">
                            Log in
                        </a>

                        <a href="/signup" className="contribute-secondary">
                            Create account
                        </a>
                    </div>
                </div>
            </div>
        );
    }

    // ---------- LOGGED IN ----------
    return (
        <div className="contribute-container">
            <div className="contribute-card">
                <h2>Contribute a Term</h2>
                <p className="contribute-subtext">
                    Your submission will be credited to your username and
                    reviewed before publishing.
                </p>

                <form onSubmit={onSubmit}>
                    <div className="contribute-field">
                        <label>Term</label>
                        <input
                            name="term"
                            value={form.term}
                            onChange={onFieldChange}
                            placeholder="e.g., Buzzy Jar"
                            autoComplete="off"
                        />
                        {errors.term && (
                            <div className="contribute-error">
                                {errors.term}
                            </div>
                        )}
                    </div>

                    <div className="contribute-field">
                        <label>Definition</label>
                        <textarea
                            name="definition"
                            value={form.definition}
                            onChange={onFieldChange}
                            placeholder="A jar that attracts insects to its location."
                            rows={4}
                        />
                        {errors.definition && (
                            <div className="contribute-error">
                                {errors.definition}
                            </div>
                        )}
                    </div>

                    <div className="contribute-field">
                        <label>Category</label>
                        <select
                            name="category"
                            value={form.category}
                            onChange={onFieldChange}
                        >
                            {categories.map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                            ))}
                        </select>
                        {errors.category && (
                            <div className="contribute-error">
                                {errors.category}
                            </div>
                        )}
                    </div>

                    <div className="contribute-field">
                        <label>Tags (comma-separated)</label>
                        <input
                            name="tags"
                            value={form.tags}
                            onChange={onFieldChange}
                            placeholder="movement, early-game, bug catching"
                            autoComplete="off"
                        />
                    </div>

                    <div className="contribute-field">
                        <label>(Optional) PaliaPedia Link</label>
                        <input
                            name="palia_pedia_u"
                            value={form.palia_pedia_u}
                            onChange={onFieldChange}
                            placeholder="https://paliapedia.com/..."
                            autoComplete="off"
                        />
                        {errors.palia_pedia_u && (
                            <div className="contribute-error">
                                {errors.palia_pedia_u}
                            </div>
                        )}
                    </div>

                    <div className="contribute-actions">
                        <button type="submit" disabled={loading}>
                            {loading ? "Submitting..." : "Submit"}
                        </button>

                        <button
                            type="button"
                            className="contribute-secondary"
                            onClick={onClear}
                            disabled={loading}
                        >
                            Clear
                        </button>
                    </div>

                    {errors.submit && (
                        <div className="contribute-message">
                            {errors.submit}
                        </div>
                    )}
                    {msg && <div className="contribute-message">{msg}</div>}
                </form>
            </div>
        </div>
    );
}
